import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Formik } from "formik";
import * as Yup from "yup";
import Cookies from "js-cookie";
import { Helmet } from "react-helmet-async";
import {
  loginStateAction,
  getUserState,
  LoginRequest,
  getLoginState,
  clearAuthStates,
} from "../../../redux/features/auth-slice";
import { AppDispatch } from "../../../redux/store";
import Input from "../../../components/input";
import Button from "../../../components/button";
import paths from "../../../navigation/paths";
import {
  adminCat,
  cookieName,
  mgTeam,
  seniorTeam,
} from "../../../core/utils/constants";
import ToastComponent from "../../../components/toast";
import {
  clearCommonStates,
  getErrorState,
  setErrorState,
} from "../../../redux/features/common-slice";
import { clearUserStates, UserRole } from "../../../redux/features/user-slice";
import { clearOtherStates } from "../../../redux/features/other-slice";
import { clearReportStates } from "../../../redux/features/report-slice";
import { clearDReportStates } from "../../../redux/features/design-report-slice";
import { clearWeeklySummaryStates } from "../../../redux/features/weekly-summary-slice";
import { clearTaskStates } from "../../../redux/features/task-slice";
import { clearMisaStates } from "../../../redux/features/misa-slice";
import { clearDocumentStates } from "../../../redux/features/document-slice";

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const loginState = useSelector(getLoginState);
  const user = useSelector(getUserState).data;
  const errorState = useSelector(getErrorState);

  const handleLogin = async (values: LoginRequest) => {
    await dispatch(
      loginStateAction({ email: values.email, password: values.password })
    );
  };

  useEffect(() => {
    dispatch(clearOtherStates());
    dispatch(clearCommonStates());
    dispatch(clearAuthStates());
    dispatch(clearUserStates());
    dispatch(clearReportStates());
    dispatch(clearDReportStates());
    dispatch(clearWeeklySummaryStates());
    dispatch(clearTaskStates());
    dispatch(clearMisaStates());
    dispatch(clearDocumentStates());
    dispatch(setErrorState(null));
  }, [dispatch]);

  useEffect(() => {
    if (errorState) {
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  useEffect(() => {
    if (Cookies.get(cookieName) !== undefined && user !== null) {
      let path = "";
      if ([UserRole.Admin, UserRole.Supervisor].includes(user.role)) {
        if (user.category.id === adminCat.id) {
          path = `${paths.CATEGORIES}/${adminCat.name}/${adminCat.id}`;
        } else {
          path = `${paths.CATEGORIES}/${user.category.title}/${user.category.id}`;
        }
      } else {
        path = paths.COURSES_USER;
      }
      navigate(path, { replace: true });
    }
  }, [user]);

  return (
    <>
      <Helmet title="Login" />
      <section
        className="h-screen content-center bg-background-default"
        style={{
          height: "calc(100vh - 32px)",
        }}
      >
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
          <div className="w-full md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 space-y-2 sm:p-8 rounded-2xl shadow-lg bg-background-paper">
              <img className="h-12" src="/logo.png" alt="logo" />
              <Formik
                initialValues={{
                  email: "",
                  password: "",
                }}
                validationSchema={Yup.object().shape({
                  email: Yup.string()
                    .email("Must be a valid email")
                    .max(50)
                    .required("Email is required"),
                  password: Yup.string()
                    .min(8, "Must be at least 8 characters")
                    .max(20, "Must be at most 20 characters")
                    .matches(
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]).{8,}$/,
                      "Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&)"
                    )
                    .required("Password is required"),
                })}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                  handleLogin(values);
                }}
              >
                {({
                  values,
                  errors,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <form
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit}
                    className="space-y-4 md:space-y-6"
                  >
                    <Input
                      label="Email"
                      type="email"
                      id="email"
                      value={values.email}
                      onChange={handleChange}
                      errorText={errors.email}
                    />
                    <Input
                      label="Password"
                      type="password"
                      id="password"
                      value={values.password}
                      onChange={handleChange}
                      errorText={errors.password}
                    />
                    {/* <div className="flex items-center justify-end">
                    <a href="#" className="text-sm font-medium hover:underline">
                      Forgot password?
                    </a>
                  </div> */}
                    <Button
                      label="Log in"
                      type="submit"
                      loading={loginState.loading}
                      disabled={
                        Array.isArray(errors) ||
                        Object.values(errors).toString() != "" ||
                        loginState.loading
                      }
                      exClassName="w-full"
                    />
                  </form>
                )}
              </Formik>
            </div>
          </div>
        </div>
        <ToastComponent id="login" />
      </section>
    </>
  );
}
